import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Bug,
  Flag,
  LayoutDashboard,
  Library,
  LogOut,
  RefreshCw,
  Send,
  Settings2,
  ShieldCheck,
  UserCog,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Quokka } from "./Quokka";

const nav = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/users", label: "Users", icon: Users },
  { to: "/library", label: "Library", icon: Library },
  { to: "/push", label: "Push", icon: Send },
  { to: "/flags", label: "Flags & tests", icon: Flag },
  { to: "/config", label: "Config", icon: Settings2 },
  { to: "/errors", label: "Errors", icon: Bug },
  { to: "/security", label: "Security", icon: UserCog },
  { to: "/audit", label: "Audit log", icon: ShieldCheck },
];

function titleFor(pathname: string): { title: string; subtitle: string } {
  if (pathname.startsWith("/users/")) {
    return { title: "User detail", subtitle: "Profile, study history and consent" };
  }
  switch (pathname) {
    case "/":
      return { title: "Overview", subtitle: "How Flash Quiz is doing today" };
    case "/users":
      return { title: "Users", subtitle: "Every learner, searchable" };
    case "/library":
      return { title: "Library", subtitle: "Submissions, reports and publishing" };
    case "/push":
      return { title: "Push", subtitle: "Campaigns, segments and A/B copy" };
    case "/flags":
      return { title: "Flags & experiments", subtitle: "Rollouts without an app release" };
    case "/config":
      return { title: "Config", subtitle: "Operational limits and kill switches" };
    case "/errors":
      return { title: "Errors", subtitle: "Crash and error reports from the app" };
    case "/security":
      return { title: "Security", subtitle: "Two-factor authentication for staff" };
    case "/audit":
      return { title: "Audit log", subtitle: "Privileged action audit trail" };
    default:
      return { title: "Flash Quiz", subtitle: "Admin console" };
  }
}

export function Layout() {
  const { user, role, signOut } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const heading = titleFor(location.pathname);
  const initial = (user?.email?.[0] ?? "S").toUpperCase();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <Quokka size={30} />
          </div>
          <div>
            <div className="brand-name">Flash Quiz</div>
            <div className="brand-sub">Console</div>
          </div>
        </div>

        <nav>
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
            >
              <Icon size={16} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initial}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 11, fontWeight: 600 }}>
                {role ?? "staff"}
              </div>
              <div className="sidebar-email">{user?.email}</div>
            </div>
          </div>
          <button className="nav-item" onClick={() => void signOut()} style={{ width: "100%" }}>
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar-title">
            <h1>{heading.title}</h1>
            <p>{heading.subtitle}</p>
          </div>
          <div className="topbar-actions">
            <span className="badge badge-mint">
              <span className="dot" />
              live
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => void queryClient.invalidateQueries()}
              title="Refresh data"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <span className="badge badge-grape" title="Signed in as staff">
              <ShieldCheck size={12} />
              {role}
            </span>
          </div>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
